import * as SheetPrimitive from '@radix-ui/react-dialog'
import { AccessRequestFormCore } from '@/components/access-request/access-request-form-core'
import { YeshumanContactSheetHeader } from '@/components/landing/yeshuman/yeshuman-contact-sheet-header'
import { yeshumanEditorialSm, yeshumanUiSans } from '@/components/landing/yeshuman/yeshuman-typography'
import { Button } from '@/components/ui/button'
import { Sheet, SheetDescription, SheetPortal } from '@/components/ui/sheet'
import { getAccessRequestConfig } from '@/constants/signup'
import { cn } from '@/lib/utils'

/** Keep in sync with close timeout in the contact sheet provider. */
export const YESHUMAN_CONTACT_SHEET_CLOSE_MS = 300

/**
 * Pane chrome copied from Artifact Canvas; colours/type from Yes Human marketing shell.
 * Edit freely — does not affect the in-app canvas.
 */
const YESHUMAN_CONTACT_PANE_CLASS = cn(
  // Marketing tokens + Inter (same world as the landing page)
  'yeshuman-marketing-shell',
  'pointer-events-auto z-50 flex flex-col gap-0 p-0',
  'bg-[var(--marketing-bg)] text-[var(--marketing-text)]',
  'border border-[var(--marketing-border)] shadow-lg',
  'transition ease-out',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:duration-300 data-[state=open]:duration-300',
  'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
  'absolute inset-y-0 right-0 h-full min-h-0 w-full max-w-2xl overflow-hidden',
  'md:top-[12px] md:right-[12px] md:bottom-[12px] md:left-auto',
  'md:h-[calc(100%-24px)] md:w-[min(42rem,calc(100%-24px))] md:max-w-none',
  'md:rounded-lg md:shadow-md',
)

const fieldClassName = cn(
  yeshumanUiSans,
  'w-full rounded-none border border-[var(--marketing-border)] bg-[var(--marketing-bg)] px-4 py-3',
  'text-[15px] leading-snug text-[var(--marketing-text)] outline-none',
  'placeholder:text-[var(--marketing-muted)]',
  'focus-visible:border-[var(--marketing-gold)] focus-visible:outline focus-visible:outline-2',
  'focus-visible:outline-offset-2 focus-visible:outline-[var(--marketing-gold)]',
  'disabled:opacity-60',
)

type YeshumanContactSheetProps = {
  open: boolean
  formKey: number
  portalContainer: HTMLElement
  onOpenChange: (open: boolean) => void
}

/**
 * Yes Human landing contact pane — canvas layout copy, marketing type + colour.
 */
export function YeshumanContactSheet({
  open,
  formKey,
  portalContainer,
  onOpenChange,
}: YeshumanContactSheetProps) {
  const accessRequestConfig = getAccessRequestConfig()
  const title = accessRequestConfig.heading ?? 'Talk to us'

  return (
    <Sheet modal={false} open={open} onOpenChange={onOpenChange}>
      <SheetPortal container={portalContainer}>
        <SheetPrimitive.Content
          data-slot="yeshuman-contact-sheet"
          className={YESHUMAN_CONTACT_PANE_CLASS}
        >
          <div className="flex h-full min-h-0 flex-col">
            <YeshumanContactSheetHeader title={title} />
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6" key={formKey}>
                <SheetDescription className={cn(yeshumanEditorialSm, 'max-w-[36ch] text-pretty')}>
                  A working session on what you have now, what it can become, and how it gets live.
                </SheetDescription>
                <AccessRequestFormCore
                  variant="marketingTheme"
                  formClassName="flex flex-col gap-5"
                  successPanelClassName="rounded-none border border-[var(--marketing-border)] bg-[var(--yeshuman-paper-alt)] px-5 py-7"
                  errorClassName="rounded-none border border-[var(--marketing-gold)]/40 bg-[var(--marketing-gold-soft)] px-4 py-3 text-sm text-[var(--marketing-text)]"
                  marketingFieldClassName={fieldClassName}
                  formAriaLabel={title}
                  renderSubmit={({ submitting, submitLabel }) => (
                    <Button
                      type="submit"
                      variant="marketingSolid"
                      size="marketing"
                      className={cn(yeshumanUiSans, 'w-full')}
                      disabled={submitting}
                      pressFeedback
                    >
                      {submitting ? 'Sending…' : submitLabel}
                    </Button>
                  )}
                />
              </div>
            </div>
          </div>
        </SheetPrimitive.Content>
      </SheetPortal>
    </Sheet>
  )
}
